import React, { useMemo, useState, useEffect } from 'react';
import { productsAPI, productStockAPI, suppliersAPI, Product, ProductStockRow, Supplier } from '../../services/api';
import { PlusIcon, PencilIcon, TrashIcon, CubeIcon, ArchiveBoxIcon, DocumentTextIcon, EyeIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import ProductForm from './ProductForm';
import ProductStockEditor from './ProductStockEditor';
import ProductDetail from './ProductDetail';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, SearchInput, PageHero } from '../ui';

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [stockRows, setStockRows] = useState<ProductStockRow[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [stockEditorProduct, setStockEditorProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, suppliersData, stockData] = await Promise.all([
        productsAPI.getAll(),
        suppliersAPI.getAll({}),
        productStockAPI.getAll(),
      ]);
      setProducts(productsData);
      setSuppliers(suppliersData.data || suppliersData);
      setStockRows(stockData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsFormOpen(true);
  };

  const handleViewDetails = (product: Product) => {
    setDetailProduct(product);
    setIsDetailOpen(true);
  };

  const handleViewDocs = (product: Product) => {
    setDetailProduct(product);
    setIsDetailOpen(true);
  };

  const handleEditStock = (product: Product) => {
    setStockEditorProduct(product);
  };

  const handleDelete = async (id: number) => {
    try {
      await productsAPI.delete(id);
      setSuccess('Product deleted successfully');
      setDeleteConfirm(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete product');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleFormSuccess = () => {
    setSuccess('Product saved successfully');
    fetchData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const stockByProductId = useMemo(
    () => new Map(stockRows.map((row) => [row.productId, row])),
    [stockRows],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Enhanced Hero Section */}
      <PageHero
        title="Products"
        subtitle="Manage product catalog"
        icon={<ArchiveBoxIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="orange"
      >
        <div className="mt-2">
          <Button
            variant="primary"
            onClick={handleAdd}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Add Product
          </Button>
        </div>
      </PageHero>

      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-success-50 border border-success-200 text-success-600 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="mb-4">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search products by name or SKU..."
        />
      </div>

      <div className="space-y-3 md:hidden">
        {filteredProducts.map((product) => {
          const stock = stockByProductId.get(product.id);
          return (
            <Card key={product.id} variant="elevated">
              <CardBody className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{product.name}</p>
                    <p className="mt-1 text-xs text-neutral-500">{product.sku}</p>
                  </div>
                  <Badge variant={product.isActive ? 'success' : 'neutral'}>
                    {product.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Available</p>
                    <p className="mt-1 font-semibold text-neutral-900">
                      {stock?.isInventoryLinked === false ? 'Not tracked' : `${stock?.availableQuantity ?? 0} / ${stock?.totalQuantity ?? 0}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Lots</p>
                    <p className="mt-1 font-semibold text-neutral-900">{stock?.lotSummaries?.length ?? stock?.lotNumbers?.length ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Supplier</p>
                    <p className="mt-1 truncate text-neutral-700">{product.supplier?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Unit</p>
                    <p className="mt-1 text-neutral-700">{product.unit || '-'}</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  <button
                    onClick={() => handleEdit(product)}
                    className="rounded-md border border-neutral-200 p-2 text-primary-600"
                    title="Edit product"
                    aria-label={`Edit product ${product.name}`}
                  >
                    <PencilIcon className="mx-auto h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleEditStock(product)}
                    className="rounded-md border border-neutral-200 p-2 text-emerald-600"
                    title="Edit stock"
                    aria-label={`Edit stock for ${product.name}`}
                  >
                    <CircleStackIcon className="mx-auto h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleViewDetails(product)}
                    className="rounded-md border border-neutral-200 p-2 text-sky-600"
                    title="Open product overview"
                    aria-label={`Open product overview for ${product.name}`}
                  >
                    <EyeIcon className="mx-auto h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleViewDocs(product)}
                    className="rounded-md border border-neutral-200 p-2 text-cyan-600"
                    title="Open PDF documents"
                    aria-label={`Open documents for ${product.name}`}
                  >
                    <DocumentTextIcon className="mx-auto h-5 w-5" />
                  </button>
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      <Card variant="elevated" className="hidden md:block">
        <CardBody className="p-0">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Available
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Lots
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredProducts.map((product) => {
                const stock = stockByProductId.get(product.id);
                return (
                <tr key={product.id} className="hover:bg-neutral-50 cursor-pointer transition-colors">
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900"
                  >
                    {product.sku}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900"
                  >
                    {product.name}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {product.supplier?.name || '-'}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {product.category?.name || '-'}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {product.unit || '-'}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900"
                  >
                    {stock?.isInventoryLinked === false ? 'Not tracked' : `${stock?.availableQuantity ?? 0} / ${stock?.totalQuantity ?? 0}`}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {stock?.lotSummaries?.length ?? stock?.lotNumbers?.length ?? 0}
                  </td>
                  <td
                    onClick={() => handleViewDetails(product)}
                    className="px-6 py-4 whitespace-nowrap"
                  >
                    <Badge variant={product.isActive ? 'success' : 'neutral'}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                      title="Edit product"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleEditStock(product)}
                      className="text-emerald-600 hover:text-emerald-900 mr-3"
                      title="Edit stock"
                    >
                      <CircleStackIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleViewDetails(product)}
                      className="text-sky-600 hover:text-sky-900 mr-3"
                      title="Open product overview"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleViewDocs(product)}
                      className="text-cyan-600 hover:text-cyan-900 mr-3"
                      title="Open PDF documents"
                    >
                      <DocumentTextIcon className="h-5 w-5" />
                    </button>
                    {deleteConfirm === product.id ? (
                      <div className="inline-flex items-center space-x-2">
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-error-600 hover:text-error-900 text-xs"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-neutral-600 hover:text-neutral-900 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(product.id)}
                        className="text-error-600 hover:text-error-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {filteredProducts.length === 0 && (
        <EmptyState
          icon={<CubeIcon className="h-12 w-12" />}
          title="No products"
          description="Get started by creating a new product."
        />
      )}

      <ProductForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        product={selectedProduct}
      />
      <ProductStockEditor
        isOpen={Boolean(stockEditorProduct)}
        product={stockEditorProduct}
        onClose={() => setStockEditorProduct(null)}
        onSuccess={fetchData}
      />
      <ProductDetail
        isOpen={isDetailOpen}
        product={detailProduct}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailProduct(null);
        }}
      />
    </div>
  );
};

export default ProductManagement;
